import React, { useEffect, useState, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import clsx from 'clsx'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { Container } from '@material-ui/core'
import { setFullLocale } from '../utils/utils'
import { getTranslations } from '../i18n'
import Layout from '../components/Layout/Layout'
import { takeBackData } from '../typings/base'
import TakeBackBox from '../components/TakeBack/TakeBackBox'
import TakeBackHeader from '../components/TakeBack/TakeBackHeader'
import TakeBackFirstStep from '../components/TakeBack/TakeBackFirstStep'
import TakeBackSecondStep from '../components/TakeBack/TakeBackSecondStep'
import TakeBackThirdStep from '../components/TakeBack/TakeBackThirdStep'
import { postTakeBackInfo } from '../redux/actions/takeBack'
import TakeBackFaq from '../components/TakeBack/TakeBackFaq'
import useTakeBackStyle from '../components/TakeBack/TakeBack.style'
import { secondHandCarrierIdByCountry } from '../utils/utils'
import { SAVE_PRISMIC_DOCUMENTS } from '../redux/actions/prismic'
import { prismicText } from '../components/Global/CMSText'
import { createClient, getSingleDocument } from '../prismicio'
import { PrismicAccount } from '../interfaces/common/prismicAccount'
import { InferGetStaticPropsType } from 'next/types'
import { GetMapDispatchTypes } from '../interfaces/common/redux'
import { PrismicGeneralConfig } from '../interfaces/common/prismicGeneralConfig'
import { PrismicTakeBack } from '../interfaces/common/prismicTakeBack'
import TakeBackEditParcelNumberStep from '@/components/TakeBack/takeBackEditParcelNumberStep'
import { simplifiedTakeBack } from '@/utils/constants'

type TTakeBackProps = InferGetStaticPropsType<typeof getStaticProps> &
  GetMapDispatchTypes<typeof mapDispatchToProps>

const mapStateToProps = () => ({})

const mapDispatchToProps = {
  postTakeBackInfo: postTakeBackInfo,
}

const TakeBack = ({
  main,
  takeback,
  inStoreText,
  account,
  faqRes,
  postTakeBackInfo,
  basket,
  banner,
  funnel,
  lang,
}: TTakeBackProps) => {
  const classes = useTakeBackStyle()
  const router = useRouter()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [priceValue, setPriceValue] = useState(null)
  const [parcelNumber, setParcelNumber] = useState(1)
  const [currentProductIndex, setCurrentProductIndex] = useState(0)
  const [takeBackProductsInformations, setTakeBackProductsInformations] =
    useState([])
  const { trackPageView } = useMatomo()
  const [show, setShow] = useState(true)
  const [showCriterion, setShowCriterion] = useState(false)
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [isUpdateStep1, setIsUpdateStep1] = useState(false)
  const [secondStepError, setSecondStepError] = useState(false)
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    if (process.env.STORE === 'maje_2ndhand' && process.env.ENV === 'prod') {
      const currentPath = router.asPath
      if (currentPath === '/take-back' || currentPath === '/pages/vendre') {
        router.push('/pages/vente ')
      }
      if (currentPath === '/en/take-back' || currentPath === '/en/sell') {
        router.push('/en/pages/trade-in-service')
      }
    }

    if (!takeback?.data?.is_take_back_activated) {
      router.push('/')
    }

    trackPageView({ documentTitle: 'Take Back Program' })
    window?.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    dispatch(
      SAVE_PRISMIC_DOCUMENTS({
        general: main?.data,
        account: account?.data,
        instore: inStoreText?.data,
      })
    )
  }, [account, main, inStoreText])

  //mount, make first product
  useEffect(() => {
    const questionsArray = []
    takeback?.data?.questions.forEach((question) => {
      questionsArray.push({
        title: prismicText(question?.question),
        required: question.is_required,
        answer: '',
      })
    })
    handleTakeBackProductsInformations(true, false, questionsArray)
  }, [currentProductIndex, isUpdateStep1])

  const currentLocale = router?.locale ? router.locale : 'fr'
  const formattedCurrentLocale = `${currentLocale}_${currentLocale.toUpperCase()}`
  const nextStep = activeStep + 1

  const handleParcelNumberChange = (parcelNumber: number) => {
    setParcelNumber(parcelNumber)
  }

  const productStateObject = {
    perfectCondition: {
      title: prismicText(
        takeback?.data?.perfectcondition,
        t('texts:takeBack:state_1')
      ),
      desc: prismicText(
        takeback?.data?.perfectconditiondefinition,
        t('texts:takeBack:state_1')
      ),
    },
    veryGoodCondition: {
      title: prismicText(
        takeback?.data?.verygoodcondition,
        t('texts:takeBack:state_2')
      ),
      desc: prismicText(
        takeback?.data?.verygoodconditiondefinition,
        t('texts:takeBack:state_2')
      ),
    },
    goodCondition: {
      title: prismicText(
        takeback?.data?.goodcondition,
        t('texts:takeBack:state_3')
      ),
      desc: prismicText(
        takeback?.data?.goodconditiondefinition,
        t('texts:takeBack:state_3')
      ),
    },
  }

  const handleNextStep = () => {
    setActiveStep(nextStep)
  }

  const handlePrevStep = () => {
    if (activeStep === 0 && show === false) {
      setShow(true)
    }
    if (activeStep === 0 && show === false) {
      setShow(true)
    }
    if (activeStep === 1 && showLoginForm === false) {
      setActiveStep(activeStep - 1)
      setShow(false)
      setShowCriterion(true)
    }
    if (activeStep === 1 && showLoginForm === true) {
      setShowLoginForm(false)
    }
  }
  const handleAddProduct = () => {
    const newIndex = takeBackProductsInformations.length
    setCurrentProductIndex(newIndex)
    setActiveStep(0)
    setShow(true)
    setShowCriterion(false)
    setIsUpdateStep1(!isUpdateStep1)
  }

  const handleRemoveProduct = (index) => {
    const productList = [...takeBackProductsInformations]
    productList.splice(index, 1)
    setTakeBackProductsInformations(productList)
  }

  // storage of product informations at each step in order to reuse them:
  // pre-filled forms on previous step
  // send informations with handleLogisticsTakeBackData
  const handleTakeBackProductsInformations = (
    firstStepFirstPart,
    firstStepSecondPart,
    productData
  ) => {
    const takeBackProducts = [...takeBackProductsInformations]
    if (firstStepFirstPart) {
      if (takeBackProducts[currentProductIndex]) {
        takeBackProducts[currentProductIndex] = {
          ...takeBackProducts[currentProductIndex],
          answers: productData,
        }
      } else {
        takeBackProducts.push({ answers: productData })
      }
    }
    if (firstStepSecondPart) {
      takeBackProducts[currentProductIndex] = {
        ...takeBackProducts[currentProductIndex],
        ...productData,
      }
    }
    setTakeBackProductsInformations(takeBackProducts)
  }

  //add type for takeBackCustomerData and currentIncentive
  const handleLogisticsTakeBackData = async (
    takeBackCustomerData,
    currentIncentive
  ) => {
    const products = []
    takeBackProductsInformations.map((product) => {
      const amount = parseFloat(
        (product.amount + (product.amount * currentIncentive) / 100).toFixed(0)
      )
      products.push({
        lizeeSKU: product.lizeeSKU,
        labelReference: product.labelReference,
        amount: amount,
        quality: product.quality,
        withoutLabelReference: product.withoutLabelReference,
      })
    })
    const logisticsTakeBackData: takeBackData = {
      products,
      parcelNumber: parcelNumber,
      carrierId: parseInt(
        secondHandCarrierIdByCountry(takeBackCustomerData?.country)
      ),
      ...takeBackCustomerData,
    }

    const postTakeBackRequest = await postTakeBackInfo(logisticsTakeBackData)
    if (postTakeBackRequest.type === 'POST_TAKE_BACK_INFO_SUCCESS') {
      handleNextStep()
    } else {
      setSecondStepError(true)
    }
  }

  const Steps = useMemo(() => {
    const steps = [
      <TakeBackFirstStep
        key="first-step"
        handleNextStep={handleNextStep}
        prismicTakeBack={takeback}
        t={t}
        priceValue={priceValue}
        setPriceValue={setPriceValue}
        handleTakeBackProductsInformations={handleTakeBackProductsInformations}
        show={show}
        setShow={setShow}
        takeBackProductsInformations={takeBackProductsInformations}
        currentProductIndex={currentProductIndex}
        showCriterion={showCriterion}
        setShowCriterion={setShowCriterion}
        productStateObject={productStateObject}
        main={main}
        lang={lang}
      />,
    ]

    if (takeback?.data?.show_edit_parcel_number) {
      steps.push(
        <TakeBackEditParcelNumberStep
          key="parcel-edit"
          parcelNumber={parcelNumber}
          onChange={handleParcelNumberChange}
          handleNextStep={handleNextStep}
        />
      )
    }

    steps.push(
      <TakeBackSecondStep
        key="second-step"
        takeback={takeback}
        t={t}
        account={account}
        takeBackProductsInformations={takeBackProductsInformations}
        priceValue={priceValue}
        handleRemoveProduct={handleRemoveProduct}
        handleAddProduct={handleAddProduct}
        handleLogisticsTakeBackData={handleLogisticsTakeBackData}
        formattedCurrentLocale={formattedCurrentLocale}
        productStateObject={productStateObject}
        showLoginForm={showLoginForm}
        setShowLoginForm={setShowLoginForm}
        main={main}
        secondStepError={secondStepError}
        funnel={funnel}
      />
    )

    steps.push(
      <TakeBackThirdStep
        key="third-step"
        takeback={takeback}
        t={t}
        showVoucherPrintingButton={takeback?.data?.show_voucher_printing_button}
      />
    )
    return steps
  }, [
    takeback,
    t,
    account,
    priceValue,
    takeBackProductsInformations,
    handleNextStep,
    handleRemoveProduct,
    handleAddProduct,
    handleLogisticsTakeBackData,
    showLoginForm,
    currentProductIndex,
    show,
    showCriterion,
    formattedCurrentLocale,
    productStateObject,
    secondStepError,
    main,
    lang,
  ])

  return (
    <Layout
      mainLogo={main?.data?.logo}
      doc={main}
      metaTitle={prismicText(
        takeback?.data?.meta_title_take_back,
        t('texts:takeBack:meta_title')
      )}
      hideTop
      inStoreText={inStoreText}
      t={t}
      metaDescription={takeback?.data?.meta_description_take_back}
      metaKeywords={takeback?.data?.meta_keywords_take_back}
      basket={basket}
      takeback={takeback}
      account={account}
      faq={faqRes}
      banner={banner}
    >
      <main className={clsx('takeBack', classes.root)}>
        <Container
          maxWidth={'xl'}
          className={clsx('takeBack__container', classes.container)}
        >
          <div className={clsx('takeBack__content', classes.content)}>
            <TakeBackHeader
              settings={takeback}
              handlePrevStep={handlePrevStep}
              t={t}
              activeStep={activeStep}
              show={show}
            />
            <TakeBackBox
              data={takeback?.data}
              t={t}
              activeStep={activeStep}
              hideStep2={simplifiedTakeBack}
              additionalEditParcelNumberStep={
                takeback?.data?.show_edit_parcel_number
              }
            >
              {Steps[activeStep]}
            </TakeBackBox>
            <div className={clsx(classes.faqSection, 'takeBack-faq__section')}>
              <TakeBackFaq takeback={takeback} t={t} />
            </div>
          </div>
        </Container>
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale, previewData }) {
  const client = createClient({ previewData })
  const lang = setFullLocale(locale)

  const takeback: PrismicTakeBack | null = await getSingleDocument(
    client,
    lang,
    'take_back'
  )
  const general: PrismicGeneralConfig | null = await getSingleDocument(
    client,
    lang,
    'general'
  )
  const account: PrismicAccount | null = await getSingleDocument(
    client,
    lang,
    'account'
  )
  const inStoreText = await getSingleDocument(client, lang, 'instore')
  const basket = await getSingleDocument(client, lang, 'basket')
  const faqRes = await getSingleDocument(client, lang, 'faq')
  const banner = await getSingleDocument(client, lang, 'banner')
  const funnel = await getSingleDocument(client, lang, 'funnel')

  return {
    props: {
      takeback,
      main: general,
      account,
      inStoreText,
      faqRes,
      basket,
      banner,
      funnel,
      i18nResources: await getTranslations(locale, ['texts']),
      lang,
    },
    revalidate: 300,
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TakeBack)
